import { useEffect, useState, type ReactNode } from 'react';
import { TABLE_THEME_MANIFEST, useTableThemeStore } from '../state/table-theme-store.js';

interface ThemeManifest {
  readonly v: number;
  readonly name: string;
  readonly backdrop?: string;
  readonly tableSurface?: string;
  readonly tint?: string;
}

const manifestCache = new Map<string, ThemeManifest | null>();

async function loadManifest(theme: Exclude<keyof typeof TABLE_THEME_MANIFEST, never>): Promise<ThemeManifest | null> {
  const file = TABLE_THEME_MANIFEST[theme];
  if (manifestCache.has(theme)) return manifestCache.get(theme) ?? null;
  try {
    const res = await fetch(file);
    if (!res.ok) throw new Error(String(res.status));
    const m = (await res.json()) as ThemeManifest;
    if (m.v !== 1) throw new Error('bad version');
    manifestCache.set(theme, m);
    return m;
  } catch {
    // A corrupt or absent manifest falls back to the stock look (the spec's contract).
    manifestCache.set(theme, null);
    return null;
  }
}

/**
 * Surface-theme scope (docs/proposals/themable-surfaces.md): wraps a game screen that supports
 * theming, sets the table-surface custom properties from the active theme's manifest, and renders
 * the backdrop behind content. The default theme renders nothing at all — the stock look, byte for
 * byte. Panels opt in with `data-surface="table"`; the surface sits under the existing glass.
 */
export function SurfaceTheme({ children }: { children: ReactNode }) {
  const theme = useTableThemeStore((s) => s.theme);
  const [manifest, setManifest] = useState<ThemeManifest | null>(null);

  useEffect(() => {
    let live = true;
    if (theme === 'default') {
      setManifest(null);
      return;
    }
    void loadManifest(theme as 'pirate').then((m) => {
      if (live) setManifest(m);
    });
    return () => {
      live = false;
    };
  }, [theme]);

  if (theme === 'default' || !manifest) return <>{children}</>;
  return (
    <div className="relative" data-table-theme={theme}>
      {manifest.backdrop && (
        <img
          src={manifest.backdrop}
          alt=""
          className="pointer-events-none absolute inset-0 size-full scale-110 object-cover opacity-25 blur-md"
        />
      )}
      <div className="relative" style={manifest.tableSurface ? { ['--table-surface' as string]: `url(${manifest.tableSurface})` } : undefined}>
        {children}
      </div>
    </div>
  );
}
